/**
 * Matches a DICOM UID in either of the two shapes this app encounters.
 * DICOM UIDs are PHI under common HIPAA readings — they can re-identify
 * patients via cross-referencing with PACS or planning systems.
 *
 * 1. `2.25.<decimal>` — the UUID-derived form every locally generated
 *    UID uses (dicomUid.ts, DicomProcessor): 3 segments, 38+ digit
 *    tail. The 6-digit floor spares version-like strings ("2.25.3").
 *      2.25.788614968596450049481413279112204074960
 * 2. Org-prefixed UIDs with 5+ dot-separated numeric segments, as used
 *    by the imaging / planning chain we interoperate with. The 5-segment
 *    floor keeps the regex from eating semver ("1.0.2") or IPv4
 *    addresses ("10.0.0.1").
 *      1.2.840.10008.5.1.4.1.1.481.3.123
 *      1.3.6.1.4.1.14519.5.2.1.99999.7311.298473
 */
const DICOM_UID_PATTERN = /\b2\.25\.[0-9]{6,}\b|\b[0-9]+(?:\.[0-9]+){4,}\b/g;
const REDACTED_UID = '[REDACTED_UID]';

/**
 * Recursion cap for the walker. Telemetry payloads (Sentry events,
 * breadcrumbs) are shallow; anything deeper is runaway data we cut off
 * rather than risk a stack overflow inside the telemetry hook.
 */
const MAX_DEPTH = 32;
const CIRCULAR_PLACEHOLDER = '[Circular]';
const MAX_DEPTH_PLACEHOLDER = '[MaxDepth]';

function redactString(value: string): string {
    return value.replace(DICOM_UID_PATTERN, REDACTED_UID);
}

function walk(value: unknown, seen: WeakSet<object>, depth: number): unknown {
    if (typeof value === 'string') {
        return redactString(value);
    }
    if (value === null || typeof value !== 'object') {
        return value;
    }
    // Binary buffers carry no UID strings — pass through by reference
    // rather than cloning them into useless plain objects.
    if (ArrayBuffer.isView(value) || value instanceof ArrayBuffer) {
        return value;
    }
    if (depth >= MAX_DEPTH) {
        return MAX_DEPTH_PLACEHOLDER;
    }
    if (seen.has(value)) {
        return CIRCULAR_PLACEHOLDER;
    }
    seen.add(value);

    // Error message/stack are non-enumerable and would clone to {},
    // destroying crash diagnostics. Rebuild on the original prototype
    // with redacted message/stack; never mutate the original — the same
    // instance may still be in use by application code.
    if (value instanceof Error) {
        const clone = Object.create(Object.getPrototypeOf(value)) as Error;
        clone.name = redactString(value.name);
        clone.message = redactString(value.message);
        if (value.stack !== undefined) {
            clone.stack = redactString(value.stack);
        }
        for (const [k, v] of Object.entries(value)) {
            (clone as unknown as Record<string, unknown>)[k] = walk(
                v,
                seen,
                depth + 1
            );
        }
        return clone;
    }

    if (Array.isArray(value)) {
        return value.map((v) => walk(v, seen, depth + 1));
    }

    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value)) {
        out[k] = walk(v, seen, depth + 1);
    }
    return out;
}

/**
 * Recursively walks an arbitrary JSON-like value and rewrites every
 * embedded DICOM UID substring to `[REDACTED_UID]`. Object/array
 * structure is preserved; only string contents change. Cyclic
 * references and over-deep structures are replaced with placeholder
 * strings so the walker always terminates (it runs on raw console
 * arguments via Sentry's breadcrumb hook).
 *
 * Intended for outbound telemetry (Sentry events, breadcrumbs) so
 * UIDs never leave the browser, even via console capture.
 */
export function redactDicomUids<T>(value: T): T {
    return walk(value, new WeakSet(), 0) as T;
}
